import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/hooks";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AppRoutes } from "@/constants";
import logoUrl from "@/components/icons/apaato_logo_light.svg";
import type { BaseComponentProps } from "@/components/base/BaseComponent";

// =============================================================================
// FIXED CONSTANTS - These ensure consistent header sizing across all pages
// =============================================================================

/** Fixed header height in pixels (py-4 = 16px * 2 + 40px logo = ~72px total) */
export const HEADER_HEIGHT = 72;

/** Fixed z-index for header */
const HEADER_Z_INDEX = "z-50";

/** Fixed max-width classes for header container */
const HEADER_MAX_WIDTH = "max-w-7xl 2xl:max-w-[1600px] 3xl:max-w-[1800px]";

/** Fixed padding classes */
const HEADER_PADDING = "px-4 sm:px-6 lg:px-8 2xl:px-12 py-4";

// =============================================================================
// TYPES
// =============================================================================

export interface HeaderProps extends BaseComponentProps {
  /**
   * Header positioning
   * - sticky: Sticks to top when scrolling (default)
   * - fixed: Fixed at top, content flows under
   * - relative: Normal document flow
   */
  position?: "sticky" | "fixed" | "relative";

  /**
   * Visual variant
   * - solid: White background with optional shadow/border
   * - transparent: No background
   * - scroll-aware: Transparent at top, becomes solid on scroll
   */
  variant?: "solid" | "transparent" | "scroll-aware";

  /** Show bottom border */
  showBorder?: boolean;

  /** Show shadow effect */
  showShadow?: boolean;

  // === LEFT SLOT ===
  /** Custom content to render before the logo (e.g., back button) */
  leftContent?: React.ReactNode;

  /** Show logo image (default: true) */
  showLogo?: boolean;

  /** Show "Apaato" text next to logo (default: true) */
  showLogoText?: boolean;

  // === CENTER SLOT ===
  /** Custom center content */
  centerContent?: React.ReactNode;

  // === RIGHT SLOT ===
  /** Fully custom right content (overrides showLanguageSwitcher/showAuthButton) */
  rightContent?: React.ReactNode;

  /** Show language switcher (default: true, ignored if rightContent provided) */
  showLanguageSwitcher?: boolean;

  /** Show auth button - Sign In or Dashboard (default: true, ignored if rightContent provided) */
  showAuthButton?: boolean;
}

// =============================================================================
// HOOK: useScrollAwareness
// =============================================================================

function useScrollAwareness(enabled: boolean, threshold = 20): boolean {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > threshold);
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [enabled, threshold]);

  return isScrolled;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const Header = ({
  className,
  "data-testid": dataTestId,
  position = "sticky",
  variant = "solid",
  showBorder = false,
  showShadow = false,
  leftContent,
  showLogo = true,
  showLogoText = true,
  centerContent,
  rightContent,
  showLanguageSwitcher = true,
  showAuthButton = true,
}: HeaderProps) => {
  const { user, signOut } = useAuth();
  const t = useTranslation();

  // Scroll awareness for scroll-aware variant
  const isScrolled = useScrollAwareness(variant === "scroll-aware");

  // Determine if header should appear "solid" (with background)
  const isSolid =
    variant === "solid" || (variant === "scroll-aware" && isScrolled);

  // Build class string
  const positionClass =
    position === "sticky"
      ? "sticky top-0"
      : position === "fixed"
        ? "fixed top-0 left-0 right-0 w-full"
        : "relative";

  const backgroundClass = isSolid ? "bg-white" : "bg-transparent";

  const borderClass = showBorder && isSolid ? "border-b border-gray-200" : "";

  const shadowClass = showShadow && isSolid ? "shadow-sm" : "";

  const transitionClass =
    variant === "scroll-aware" ? "transition-all duration-300" : "";

  const headerClasses = [
    positionClass,
    HEADER_Z_INDEX,
    backgroundClass,
    borderClass,
    shadowClass,
    transitionClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Render default right content if rightContent is not provided
  const renderRightContent = () => {
    if (rightContent !== undefined) {
      return rightContent;
    }

    return (
      <div className="flex items-center gap-4">
        {showLanguageSwitcher && <LanguageSwitcher />}
        {showAuthButton && (
          <>
            {user ? (
              <>
                <span className="text-sm text-gray-700 hidden sm:inline">
                  {user?.email}
                </span>
                <button
                  onClick={() => signOut()}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {t("signOut")}
                </button>
              </>
            ) : (
              <Link
                to={AppRoutes.login}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {t("signIn")}
              </Link>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <header className={headerClasses} data-testid={dataTestId}>
      <div
        className={`${HEADER_MAX_WIDTH} mx-auto ${HEADER_PADDING} flex justify-between items-center`}
      >
        {/* Left Section: Custom content + Logo */}
        <div className="flex items-center gap-4">
          {leftContent}

          {showLogo && (
            <Link to={AppRoutes.home} className="flex items-center gap-2">
              <img
                src={logoUrl}
                alt="Apaato logo"
                className="w-10 h-10 shrink-0"
              />
              {showLogoText && (
                <span className="text-2xl font-bold text-gray-900 hidden sm:block">
                  Apaato
                </span>
              )}
            </Link>
          )}
        </div>

        {/* Center Section (optional) */}
        {centerContent && (
          <div className="flex-1 flex justify-center">{centerContent}</div>
        )}

        {/* Right Section */}
        <div className="flex items-center gap-4 text-gray-900">
          {renderRightContent()}
        </div>
      </div>
    </header>
  );
};
